import { useState, useEffect } from 'react';
import { ArrowLeft, Plus, Trash2, Calendar, MapPin, Edit2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ClubProfileModal } from './ClubProfileModal';

interface TimeSlot {
  id: string;
  startTime: string;
  endTime: string;
  status: string;
}

interface Court {
  id: string;
  name: string;
  surfaceType: string | null;
  timeSlots?: TimeSlot[];
}

interface Club {
  id: string;
  name: string;
  logoUrl: string | null;
  coverUrl?: string | null;
  slogan?: string | null;
  address?: string | null;
  city?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  phone?: string | null;
  whatsapp?: string | null;
  email?: string | null;
  website?: string | null;
  managerId?: string | null;
  amenities?: string[];
  courts: Court[];
}

export const ClubManagement = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";
  const [clubs, setClubs] = useState<Club[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedClub, setExpandedClub] = useState<string | null>(null);
  const [token] = useState(localStorage.getItem('tt_auth_token') || '');

  const [users, setUsers] = useState<any[]>([]);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [editingClub, setEditingClub] = useState<Club | null>(null);

  const [showAddCourt, setShowAddCourt] = useState<string | null>(null);
  const [newCourtName, setNewCourtName] = useState('');
  const [newCourtSurface, setNewCourtSurface] = useState('Saibro');

  const [showAddSlots, setShowAddSlots] = useState<string | null>(null);
  const [slotDate, setSlotDate] = useState('');
  const [slotEndDate, setSlotEndDate] = useState('');
  const [slotStart, setSlotStart] = useState('06:00');
  const [slotEnd, setSlotEnd] = useState('22:00');
  const [slotType, setSlotType] = useState('LOCACAO');

  useEffect(() => {
    fetchClubs();
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/users`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      setUsers(data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchClubs = async () => {
    try {
      const res = await fetch(`${API_URL}/api/clubs`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      setClubs(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveClub = async (clubData: any) => {
    try {
      const url = clubData.id ? `${API_URL}/api/clubs/${clubData.id}` : `${API_URL}/api/clubs`;
      const method = clubData.id ? 'PUT' : 'POST';
      
      await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(clubData)
      });
      setShowProfileModal(false);
      setEditingClub(null);
      fetchClubs();
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const handleDeleteClub = async (id: string) => {
    if (!window.confirm('Excluir este clube? Todas as quadras e horários serão perdidos.')) return;
    try {
      await fetch(`${API_URL}/api/clubs/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchClubs();
    } catch (err) {
      console.error(err);
    }
  };

  const handleAddCourt = async (clubId: string) => {
    if (!newCourtName.trim()) return;
    try {
      await fetch(`${API_URL}/api/courts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ clubId, name: newCourtName, surfaceType: newCourtSurface })
      });
      setShowAddCourt(null);
      setNewCourtName('');
      fetchClubs();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteCourt = async (id: string) => {
    if (!window.confirm('Excluir esta quadra?')) return;
    try {
      await fetch(`${API_URL}/api/courts/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchClubs();
    } catch (err) {
      console.error(err);
    }
  };

  const handleGenerateSlots = async (courtId: string) => {
    if (!slotDate) return alert('Selecione uma data inicial');
    const finalDate = slotEndDate || slotDate;
    if (finalDate < slotDate) return alert('Data fim deve ser igual ou maior que a inicial');

    try {
      const slots = [];
      const start = new Date(`${slotDate}T00:00:00`);
      const end = new Date(`${finalDate}T23:59:59`);
      
      let currDay = new Date(start);
      while (currDay <= end) {
        const dayStr = currDay.toISOString().split('T')[0];
        let currentSlot = new Date(`${dayStr}T${slotStart}:00`);
        const endSlot = new Date(`${dayStr}T${slotEnd}:00`);

        while (currentSlot < endSlot) {
          const nextSlot = new Date(currentSlot.getTime() + 60 * 60 * 1000); // 1 hour
          slots.push({
            startTime: currentSlot.toISOString(),
            endTime: nextSlot.toISOString(),
            isRankingOnly: slotType === 'RANKING',
            type: slotType
          });
          currentSlot = nextSlot;
        }
        currDay.setDate(currDay.getDate() + 1);
      }

      await fetch(`${API_URL}/api/timeslots`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ courtId, slots })
      });
      setShowAddSlots(null);
      alert('Horários gerados com sucesso!');
    } catch (err) {
      console.error(err);
      alert('Erro ao gerar horários');
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1A1025 0%, #2D1E4B 100%)',
      padding: '24px',
      paddingTop: '160px',
      paddingBottom: '100px',
      fontFamily: "'Inter', sans-serif"
    }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '32px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button
              onClick={() => navigate('/')}
              style={{
                background: 'rgba(255,255,255,0.1)',
                border: 'none',
                color: 'white',
                width: '40px',
                height: '40px',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer'
              }}
            >
              <ArrowLeft size={20} />
            </button>
            <h1 style={{ color: 'white', fontSize: '24px', fontWeight: 'bold', margin: 0 }}>Gestão de Clubes</h1>
          </div>
          {currentUser?.role === 'ADMIN' && (
            <button
              onClick={() => { setEditingClub(null); setShowProfileModal(true); }}
              style={{
                background: 'var(--primary-color)',
                border: 'none',
                padding: '10px 16px',
                borderRadius: '8px',
                color: 'white',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer'
              }}
            >
              <Plus size={18} /> Novo Clube
            </button>
          )}
        </div>

        {/* Loading / Empty */}
        {loading ? (
          <div style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', padding: '40px' }}>Carregando clubes...</div>
        ) : clubs.length === 0 ? (
          <div style={{
            background: 'rgba(255,255,255,0.05)',
            border: '1px dashed rgba(255,255,255,0.2)',
            borderRadius: '16px',
            padding: '48px',
            textAlign: 'center'
          }}>
            <MapPin size={48} color="rgba(255,255,255,0.2)" style={{ marginBottom: '16px' }} />
            <h3 style={{ color: 'white', margin: '0 0 8px 0' }}>Nenhum clube cadastrado</h3>
            <p style={{ color: 'rgba(255,255,255,0.5)', margin: 0 }}>Adicione seu primeiro clube para gerenciar quadras e horários.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {clubs.map(club => (
              <div key={club.id} style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '12px',
                overflow: 'hidden'
              }}>
                <div 
                  style={{
                    padding: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    background: expandedClub === club.id ? 'rgba(255,255,255,0.02)' : 'transparent'
                  }}
                  onClick={() => setExpandedClub(expandedClub === club.id ? null : club.id)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{
                      width: '48px', height: '48px', borderRadius: '12px',
                      background: club.logoUrl ? `url(${club.logoUrl}) center/cover` : 'linear-gradient(135deg, #00F0FF, #00B4D8)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#2D1E4B', fontWeight: 'bold', fontSize: '20px'
                    }}>
                      {!club.logoUrl && (club.name ? (club.name.trim().split(/\s+/).length === 1 ? club.name.trim().split(/\s+/)[0].charAt(0).toUpperCase() : (club.name.trim().split(/\s+/)[0].charAt(0) + club.name.trim().split(/\s+/)[club.name.trim().split(/\s+/).length - 1].charAt(0)).toUpperCase()) : '?')}
                    </div>
                    <div>
                      <h3 style={{ color: 'white', margin: '0 0 4px 0', fontSize: '18px' }}>{club.name}</h3>
                      <p style={{ color: 'rgba(255,255,255,0.5)', margin: 0, fontSize: '14px' }}>
                        {club.courts.length} quadras cadastradas
                      </p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingClub(club);
                        setShowProfileModal(true);
                      }}
                      style={{
                        background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white',
                        width: '36px', height: '36px', borderRadius: '8px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer'
                      }}
                      title="Editar Clube"
                    >
                      <Edit2 size={16} />
                    </button>
                    {currentUser?.role === 'ADMIN' && (
                      <button 
                        onClick={(e) => { e.stopPropagation(); handleDeleteClub(club.id); }}
                        style={{ background: 'transparent', border: 'none', color: '#EF4444', cursor: 'pointer', padding: '8px' }}
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>
                </div>

                {expandedClub === club.id && (
                  <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', padding: '20px', background: 'rgba(0,0,0,0.2)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                      <h4 style={{ color: 'white', margin: 0 }}>Quadras</h4>
                      <button 
                        onClick={() => setShowAddCourt(club.id)}
                        style={{ background: 'rgba(255,255,255,0.1)', border: 'none', padding: '6px 12px', borderRadius: '6px', color: 'white', fontSize: '12px', cursor: 'pointer' }}
                      >
                        + Adicionar Quadra
                      </button>
                    </div>

                    {club.courts.length === 0 ? (
                      <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', margin: 0 }}>Nenhuma quadra adicionada ainda.</p>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
                        {club.courts.map(court => (
                          <div key={court.id} style={{ background: 'rgba(255,255,255,0.05)', padding: '16px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <div>
                                <h5 style={{ color: 'white', margin: '0 0 4px 0', fontSize: '16px' }}>{court.name}</h5>
                                <span style={{ background: 'rgba(0,240,255,0.1)', color: '#00F0FF', padding: '2px 8px', borderRadius: '4px', fontSize: '12px' }}>
                                  {court.surfaceType}
                                </span>
                              </div>
                              <button onClick={() => handleDeleteCourt(court.id)} style={{ background: 'transparent', border: 'none', color: '#EF4444', cursor: 'pointer' }}>
                                <Trash2 size={16} />
                              </button>
                            </div>
                            
                            <button 
                              onClick={() => setShowAddSlots(court.id)}
                              style={{ width: '100%', marginTop: '16px', background: 'rgba(255,255,255,0.1)', border: 'none', padding: '8px', borderRadius: '6px', color: 'white', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', cursor: 'pointer' }}
                            >
                              <Calendar size={14} /> Gerar Horários
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      <ClubProfileModal 
        isOpen={showProfileModal}
        onClose={() => { setShowProfileModal(false); setEditingClub(null); }}
        onSave={handleSaveClub}
        initialData={editingClub}
        users={users}
      />
      {showAddCourt && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div style={{ background: '#2D1E4B', padding: '24px', borderRadius: '16px', width: '100%', maxWidth: '400px' }}>
            <h3 style={{ color: 'white', margin: '0 0 16px 0' }}>Nova Quadra</h3>
            <input 
              value={newCourtName} onChange={e => setNewCourtName(e.target.value)}
              placeholder="Nome da Quadra (ex: Quadra 1)"
              style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', marginBottom: '12px' }}
            />
            <select
              value={newCourtSurface} onChange={e => setNewCourtSurface(e.target.value)}
              style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', marginBottom: '16px' }}
            >
              <option value="Saibro" style={{ color: 'black' }}>Saibro</option>
              <option value="Rápida (Dura)" style={{ color: 'black' }}>Rápida (Dura)</option>
              <option value="Grama" style={{ color: 'black' }}>Grama</option>
            </select>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setShowAddCourt(null)} style={{ flex: 1, padding: '12px', borderRadius: '8px', border: 'none', background: 'rgba(255,255,255,0.1)', color: 'white' }}>Cancelar</button>
              <button onClick={() => handleAddCourt(showAddCourt)} style={{ flex: 1, padding: '12px', borderRadius: '8px', border: 'none', background: 'var(--primary-color)', color: 'white', fontWeight: 'bold' }}>Salvar</button>
            </div>
          </div>
        </div>
      )}

      {showAddSlots && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div style={{ background: '#2D1E4B', padding: '24px', borderRadius: '16px', width: '100%', maxWidth: '400px' }}>
            <h3 style={{ color: 'white', margin: '0 0 16px 0' }}>Gerar Horários Livres</h3>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '14px', marginBottom: '4px' }}>Data Inicial</label>
                <input 
                  type="date"
                  value={slotDate} onChange={e => setSlotDate(e.target.value)}
                  style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white' }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '14px', marginBottom: '4px' }}>Até a Data (opcional)</label>
                <input 
                  type="date"
                  value={slotEndDate} onChange={e => setSlotEndDate(e.target.value)}
                  style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white' }}
                />
              </div>
            </div>
            
            <label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '14px', marginBottom: '4px' }}>Tipo de Disponibilidade</label>
            <select
              value={slotType} onChange={e => setSlotType(e.target.value)}
              style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', marginBottom: '12px' }}
            >
              <option style={{ color: 'black' }} value="LOCACAO">Locação Avulsa</option>
              <option style={{ color: 'black' }} value="RANKING">Ranking</option>
              <option style={{ color: 'black' }} value="AULA">Aula</option>
              <option style={{ color: 'black' }} value="TREINAMENTO">Treinamento Competitivo</option>
            </select>

            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '14px', marginBottom: '4px' }}>Início</label>
                <input 
                  type="time" value={slotStart} onChange={e => setSlotStart(e.target.value)}
                  style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white' }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '14px', marginBottom: '4px' }}>Fim</label>
                <input 
                  type="time" value={slotEnd} onChange={e => setSlotEnd(e.target.value)}
                  style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white' }}
                />
              </div>
            </div>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', marginBottom: '16px' }}>Serão gerados blocos de 1 hora no intervalo informado.</p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setShowAddSlots(null)} style={{ flex: 1, padding: '12px', borderRadius: '8px', border: 'none', background: 'rgba(255,255,255,0.1)', color: 'white' }}>Cancelar</button>
              <button onClick={() => handleGenerateSlots(showAddSlots)} style={{ flex: 1, padding: '12px', borderRadius: '8px', border: 'none', background: 'var(--primary-color)', color: 'white', fontWeight: 'bold' }}>Gerar</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
